import { PromiseExtended } from 'dexie'
import { db } from '../../../db'
import { useGetById, useGetQuery } from '../../../db/hooks'
import { ListResponse, TABLE_NOTIFICATIONS } from '../../../db/types'
import { ResourceType } from '../../../shared/utils/resourceTypes'
import { updateDatabase } from '../../../shared/utils/syncUtil'
import { Notification } from '../types'

function generateIndexedSort(notification: Notification) {
  return notification.sort ?? ''
}

const overrideNotifications = async (
  notifications: Notification[]
): Promise<void> => {
  const enrichedNotifications = notifications.map((notification) => {
    return {
      ...notification,
      sortIndexed: generateIndexedSort(notification),
    }
  })

  await updateDatabase(db, TABLE_NOTIFICATIONS, enrichedNotifications)
}

const overrideNotification = async (
  notification: Notification
): Promise<void> => {
  await db.table(TABLE_NOTIFICATIONS).put({
    ...notification,
    sortIndexed: generateIndexedSort(notification),
  })
}

const useGetFilteredNotificationsForUser = (
  userId: string
): ListResponse<Notification> => {
  return useGetQuery<Notification[]>(() => {
    return db
      .table<Notification>(TABLE_NOTIFICATIONS)
      .where('userId')
      .equals(userId)
      .and(
        (notification) =>
          !notification.archived && notification.visibility.dashboard
      )
      .sortBy('sortIndexed')
  }, [userId])
}

const getNotificationsByResource = (
  resourceType: ResourceType,
  resourceId: string
): PromiseExtended<Notification[]> => {
  return db
    .table<Notification>(TABLE_NOTIFICATIONS)
    .where('[referencedResourceType+referencedResourceId]')
    .equals([resourceType, resourceId])
    .and((notification) => !notification.archived)
    .sortBy('sortIndexed')
}

const useGetNotificationsByResource = (
  resourceType: ResourceType,
  resourceId: string
): ListResponse<Notification> => {
  return useGetQuery<Notification[]>(
    () => getNotificationsByResource(resourceType, resourceId),
    [resourceType, resourceId]
  )
}

const useGetNotificationsByResources = (
  resourceType: ResourceType,
  resourceIds: string[]
): ListResponse<Notification> => {
  return useGetQuery<Notification[]>(() => {
    if (resourceIds.length === 0) {
      return Promise.resolve([])
    }

    const keys = resourceIds.map((id) => [resourceType, id])

    return db
      .table<Notification>(TABLE_NOTIFICATIONS)
      .where('[referencedResourceType+referencedResourceId]')
      .anyOf(keys)
      .and((notification) => !notification.archived)
      .sortBy('sortIndexed')
  }, [resourceType, JSON.stringify(resourceIds)])
}

const useGetNotificationById = (id: string) =>
  useGetById<Notification>(TABLE_NOTIFICATIONS, id)

const addNotification = async (notification: Notification): Promise<void> => {
  await overrideNotification(notification)
}

const deleteNotification = async (id: string): Promise<void> => {
  await db.table(TABLE_NOTIFICATIONS).delete(id)
}

export {
  addNotification,
  deleteNotification,
  getNotificationsByResource,
  overrideNotification,
  overrideNotifications,
  useGetFilteredNotificationsForUser,
  useGetNotificationById,
  useGetNotificationsByResource,
  useGetNotificationsByResources,
}
